import { useEffect } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { useToasts, type ToastItem } from "@/stores/toast";

const ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle };
const TONES = {
  info: "text-cyan",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

function ToastCard({ toast }: { toast: ToastItem }) {
  const dismiss = useToasts((s) => s.dismiss);
  const Icon = ICONS[toast.tone];
  useEffect(() => {
    const t = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(t);
  }, [toast.id, toast.duration, dismiss]);
  return (
    <div
      role={toast.tone === "danger" ? "alert" : "status"}
      className="pointer-events-auto flex w-full animate-toast-in items-start gap-3 rounded-card border border-[var(--border-glow)] bg-elevated/95 p-4 shadow-glow-2 backdrop-blur"
    >
      <Icon className={cn("mt-0.5 size-5 shrink-0", TONES[toast.tone])} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-sm text-ink-muted">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action!.onClick();
              dismiss(toast.id);
            }}
            className="mt-2 inline-flex min-h-9 items-center rounded-full bg-[var(--primary-strong)] px-4 text-sm font-medium text-white"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        className="-m-1 grid size-8 place-items-center rounded-full text-ink-muted hover:bg-fg/5 hover:text-ink"
        aria-label="Dismiss notification"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}

/** Mount once near the root. Fire toasts with `toast.success("Saved")` from anywhere. */
export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-3 top-[calc(var(--safe-top)+8px)] z-[80] flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:top-auto sm:w-96 sm:items-end"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}
